/* eslint-disable react/react-in-jsx-scope */
import { useAuth } from "@/auth/hooks/useAuth";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import useScopedDashboardAccess from "@/auth/hooks/useScopedDashboardAccess";
import { sessionManager } from "@/broadcastChannel/sessionManager";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/components/ui/use-toast";
import { langs } from "@/constants/constants";
import {
  PROJECT_QUERY_GC_TIME,
  PROJECT_QUERY_STALE_TIMES,
  projectQueryKeys,
} from "@/dashboard/projectQueryConfig";
import { useModal, useProjectStore } from "@/dashboard/store/store";
import { useIsMobile } from "@/hooks/use-mobile";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import i18n from "@/i18n";
import { DropdownMenu } from "@radix-ui/react-dropdown-menu";
import {
  Bell,
  ChevronDown,
  Eye,
  KeyRound,
  LogOut,
  SettingsIcon,
  User2,
  UserPen,
} from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ImpersonationBanner from "./ImpersonationBanner";

const EditProfileForm = lazy(() => import("./EditForm"));

//**************** header dashboard**************** //
const HeaderTop = () => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "common",
  });
  const { user, logout } = useAuth();
  const userDisplayName =
    [user?.firstname, user?.lastname]
      .map((value) => (typeof value === "string" ? value.trim() : ""))
      .filter(Boolean)
      .join(" ") ||
    (typeof user?.Organisation?.name === "string"
      ? user.Organisation.name.trim()
      : "") ||
    (typeof user?.structureName === "string" ? user.structureName.trim() : "") ||
    (typeof user?.fullname === "string" ? user.fullname.trim() : "");
  // const { updateUserState } = useAuth();
  const { setOpenModal } = useModal();
  const { project } = useProjectStore();
  const page = window.location.pathname;
  const pageSearch = window.location.search;
  const projectPageMatch = page.match(/^\/dashboard\/project\/(\d+)/);
  const projectIdForSummary = projectPageMatch?.[1] ?? null;
  const isProjectPage = Boolean(projectIdForSummary);
  const { data: projectSummary } = useFetchDataV2<any>(
    projectIdForSummary
      ? `${import.meta.env.VITE_API_ENDPOINT}/project/${projectIdForSummary}/summary`
      : "",
    {
      cacheId: projectIdForSummary
        ? projectQueryKeys.summary(projectIdForSummary)
        : "project-summary",
      staleTime: PROJECT_QUERY_STALE_TIMES.summary,
      gcTime: PROJECT_QUERY_GC_TIME,
      browserCache: true,
      browserCacheNamespace: "project-summary",
      browserCacheTtlMs: 1000 * 60 * 60,
    },
  );
  const summaryProgram = projectSummary?.program;
  const headerOwnerCarrierId = isProjectPage
    ? Number(summaryProgram?.carrier?.id ?? (project as any)?.program?.carrier?.id ?? 0)
    : undefined;
  const { canManageScope } = useScopedDashboardAccess(headerOwnerCarrierId);
  const currentProgramId = useMemo(() => {
    const search = new URLSearchParams(pageSearch);
    const match = page.match(/^\/dashboard\/program\/(\d+)/);
    if (match?.[1]) {
      const parsed = Number(match[1]);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }

    const queryProgramId = Number(search.get("programId"));
    if (Number.isFinite(queryProgramId) && queryProgramId > 0) {
      return queryProgramId;
    }

    const summaryProgramId = Number(summaryProgram?.id);
    if (Number.isFinite(summaryProgramId) && summaryProgramId > 0) {
      return summaryProgramId;
    }

    const storeProgramId = Number((project as any)?.program?.id);
    if (Number.isFinite(storeProgramId) && storeProgramId > 0) {
      return storeProgramId;
    }

    return undefined;
  }, [page, pageSearch, project, summaryProgram?.id]);
  const currentProgramName = useMemo(() => {
    const search = new URLSearchParams(pageSearch);
    const queryProgramName = search.get("programName");
    if (queryProgramName && queryProgramName.trim().length > 0) {
      return queryProgramName.trim();
    }

    if (typeof summaryProgram?.name === "string" && summaryProgram.name.trim().length > 0) {
      return summaryProgram.name.trim();
    }

    const storeProgramName = (project as any)?.program?.name;
    if (typeof storeProgramName === "string" && storeProgramName.trim().length > 0) {
      return storeProgramName.trim();
    }

    if (!currentProgramId) return undefined;
    const programTitleElement = document.getElementById("program-title");
    const rawText = programTitleElement?.textContent?.trim() ?? "";
    if (!rawText) return undefined;
    return rawText.replace(/^Programme\s*/i, "").trim() || undefined;
  }, [currentProgramId, pageSearch, project, summaryProgram?.name]);
  const openCreateProjectModal = () => {
    if (!currentProgramId) return;

    setOpenModal({
      name: "projectCreationChoice",
      value: true,
      programId: currentProgramId,
      programName: currentProgramName,
    });
  };

  const isMobile = useIsMobile();
  const [openEditProfil, setOpenEditProfil] = useState(false);
  const [openProfilePopover, setOpenProfilePopover] = useState(false);

  // const [firstname, setFirstname] = useState(user?.firstname);
  // const [lastname, setLastname] = useState(user?.lastname);
  // const [structureName, setStructureName] = useState(user?.structureName);
  // const [email, setEmail] = useState(user?.email);
  // const [password, setPassword] = useState("");
  // const [confirmPassword, setConfirmPassword] = useState("");

  const { postData } = usePostData();
  const hidePostActions = useHidePostActions(headerOwnerCarrierId);

  const { toast } = useToast();

  const { fetchData } = useFetchDataV2("");
  const [openNotifications, setOpenNotifications] = useState(false);
  const [loadingReadNotification, setLoadingReadNotification] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [countNotification, setCountNotification] = useState(0);
  const photoUrl = user?.photo?.uri ?? "";
  const [step, setStep] = useState(1);
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(1);
  const [minutes, setMinutes] = useState(0);

  const isIndicatorNotification = (notification: any) =>
    Number(notification?.type) === 1;

  const isProjectNotification = (notification: any) =>
    [2, 3, 4].includes(Number(notification?.type));

  const getNotificationLabel = (notification: any) => {
    if (isIndicatorNotification(notification)) {
      const indicators = Array.isArray(notification.indicator)
        ? notification.indicator
        : [];
      const translatedIndicator = indicators.find(
        (notif: any) => notif?.lang?.code === i18n.language,
      );
      const indicatorName =
        translatedIndicator?.name ??
        indicators[0]?.name ??
        notification?.indicator?.name;

      if (indicatorName || notification.projet) {
        return t("indNotif", {
          indicator: indicatorName ?? "",
          project: notification.projet ?? "",
        });
      }

      return notification.message ?? "";
    }

    if (notification.type == 2) {
      return t("validNotif", { name: notification.projet });
    }

    if (notification.type == 3) {
      return t("rejetNotif", { name: notification.projet });
    }

    if (notification.type == 4) {
      return t("submittedNotif", { name: notification.projet });
    }

    return notification.message ?? "";
  };

  const getNotificationImageSrc = (notification: any) => {
    const thumb = typeof notification?.thumb === "string"
      ? notification.thumb.trim()
      : "";

    if (!thumb) {
      return "/pexels-pixabay-235925.jpg";
    }

    if (/^https?:\/\//i.test(thumb)) {
      return thumb;
    }

    return `${import.meta.env.VITE_MEDIA_BASE}${thumb}`;
  };

  // Conversion en minutes pour le backend
  const durationInMinutes = days * 1440 + hours * 60 + minutes;
  const isInvalidDuration = durationInMinutes <= 0;

  useEffect(() => {
    if (user && openNotifications) {
      fetchData(
        `${import.meta.env.VITE_IP_API}/notifications/indicator/${user?.id}`,
      ).then((data) => {
        if (data) {
          const visibleNotifications = Array.isArray(data)
            ? data.filter((notification) => isProjectNotification(notification))
            : [];
          setNotifications(visibleNotifications);
          setCountNotification(
            visibleNotifications.filter((n) => n.status === 0).length,
          );
        }
      });
    }
  }, [fetchData, openNotifications, user]);

  const saveLanguageParameter = async (value) => {
    const res = await postData({
      url: `${import.meta.env.VITE_API_ENDPOINT}/parameter/save`,
      data: {
        user: user?.id,
        project: null,
        name: "lang",
        value: value,
      },
    });
    if (res === null) {
      return;
    }

    // setAuthState({ user: res, loading: false, error: null });

    // setLoadingEditProfil(false);

    toast({
      title: t("Choix de langue enregistré"),
      duration: 1000,
    });
  };

  return (
    <div className="flex flex-wrap justify-between py-4 items-center">
      {page.search("programs") >= 1 ||
        page === "/dashboard/" ||
        page === "/dashboard" ? (
        <>
          {!hidePostActions && user?.roles?.includes("ROLE_CARRIER") ? (
            <button
              className={`h-auto py-2 font-normal ${buttonVariants({
                variant: "default",
              })}`}
              style={{ borderRadius: "50px" }}
              onClick={() => setOpenModal({ name: "program", value: true })}
            >
              {t("Ajouter un programme")}
            </button>
          ) : (
            <div className={`h-auto py-2 `}></div>
          )}
        </>
      ) : (
        <>
          {!hidePostActions &&
          (canManageScope ||
            (isProjectPage && user?.roles?.includes("ROLE_CARRIER"))) ? (
            <button
              className={`h-auto py-2 font-normal ${buttonVariants({
                variant: "default",
              })}`}
              style={{ borderRadius: "50px" }}
              onClick={openCreateProjectModal}
              disabled={!currentProgramId}
            >
              {t("Ajouter un projet")}
            </button>
          ) : (
            <div className={`h-auto py-2 `}></div>
          )}
        </>
      )}

      {import.meta.env.VITE_ENV !== undefined &&
        (import.meta.env.VITE_ENV === "DEV" ||
          import.meta.env.VITE_ENV === "dev") && (
          <p className="text-[#f00] text-[16px] font-bold">
            Environnement: {import.meta.env.VITE_ENV}
          </p>
        )}
      <div>
        <ImpersonationBanner />
      </div>
      <div className="flex items-center gap-5">
        {hidePostActions && (
          <Popover>
            <PopoverTrigger asChild>
              <div className="flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 shadow-sm cursor-pointer">
                <Eye className="size-4" />
                {"Mode lecture seule"}
              </div>
            </PopoverTrigger>
            <PopoverContent className="max-w-[260px] text-xs">
              Vous êtes en lecture seule : vous pouvez consulter les
              informations, mais aucune modification n’est autorisée.
            </PopoverContent>
          </Popover>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild className="cursor-pointer">
            <div className="flex items-center">
              {i18n === undefined ||
                i18n === null ||
                i18n.language === undefined ||
                i18n.language === null
                ? "FR"
                : i18n.language.split("-")[0].toUpperCase()}
              {/* <span className={"fi fi-"+(i18n.language === "en" ? "gb" : (i18n.language === "fr-FR" ? "fr" : i18n.language))} style={{
                    width: '45px',
                    height: '25px',
                    backgroundSize: 'contain',
                  }}></span> */}
              <ChevronDown className="size-4 opacity-50" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-[10px]">
            <DropdownMenuGroup className="w-[50px]">
              {langs.map((lang, key) => (
                <DropdownMenuItem
                  className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                  onClick={() => {
                    i18n.language !== lang && i18n.changeLanguage(lang);
                    saveLanguageParameter(lang);
                  }}
                  key={key}
                >
                  {lang.toUpperCase()}
                  {/* <span className={"fi fi-"+(lang === "en" ? "gb" : lang)+(i18n.language === lang ? " opacity-70 cursor-not-allowed" : "")} style={{
                                width: '45px',
                                height: '25px',
                                backgroundSize: 'contain',
                              }}></span> */}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        {/* <Button variant={"roundedOrange"} className="h-auto py-2 font-normal">
        { t("Carte des projets")}
        </Button> */}

        <AlertDialog
          open={openNotifications}
          onOpenChange={setOpenNotifications}
        >
          <AlertDialogTrigger asChild>
            <Link
              onClick={(e) => {
                e.preventDefault();

                setCountNotification(0);

                postData({
                  url: `${import.meta.env.VITE_IP_API}/notification/read-all`,
                  data: {
                    ids: notifications
                      .filter((n: any) => n.status === 0)
                      .map((n: any) => n.id),
                  },
                });

                setOpenNotifications(true);
              }}
              to={`/dashboard`}
              className={`relative flex cursor-pointer items-center`}
            >
              <Bell size={20} />{" "}
              {
                <>
                  {countNotification > 0 && !openNotifications && (
                    <div className="absolute bottom-[10px] left-[60%] w-[20px] h-[20px] text-[12px] rounded-full bg-[#f00] text-[#fff] text-center">
                      {countNotification}
                    </div>
                  )}
                </>
              }
            </Link>
          </AlertDialogTrigger>
          <AlertDialogContent>
            {loadingReadNotification && (
              <div className="fixed z-[9999999999] flex items-center justify-center top-0 left-0 w-full h-full bg-[#00000080]">
                <div className="flex gap-2 text-[#fff]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={20}
                    height={20}
                    viewBox="0 0 24 24"
                    fill="none"
                    color="#fff"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="animate-spin"
                  >
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  {t("Chargement")}...
                </div>
              </div>
            )}
            <AlertDialogHeader>
              <AlertDialogTitle>{t("Notifications")}</AlertDialogTitle>
            </AlertDialogHeader>
            <div className="mt-3 max-h-[50vh] overflow-y-auto">
              <dl className="max-w-md text-gray-900 divide-y divide-gray-200 dark:text-white dark:divide-gray-700 cursor-pointer">
                {notifications.length === 0 && (
                  <p className="m-0">{t("Aucune notification.")}</p>
                )}
                {notifications
                  .filter((notification: any) => isProjectNotification(notification))
                  .map((notification: any) => (
                    <div
                      key={notification.id}
                      className={`flex flex-col py-3 px-3 ${notification.status === 0 ? "bg-slate-100" : ""
                        }`}
                      onClick={async () => {
                        const ids = notification.id.split("_");
                        const idProject = ids[0];
                        setLoadingReadNotification(true);
                        await postData({
                          url: `${import.meta.env.VITE_IP_API
                            }/notification/read/${notification.id}`,
                          data: {},
                        });
                        const tab =
                          notification.type == 4 ? "" : "?tab=suivi-perf";
                        window.location.href = `/dashboard/project/${idProject + tab
                          }`;
                      }}
                    >
                      <dt
                        className="mb-1 text-gray-500 text-sm flex gap-4"
                        title={getNotificationLabel(notification)}
                      >
                        <div className="size-[100px] rounded-sm overflow-hidden">
                          <img
                            className="size-full object-cover"
                            src={getNotificationImageSrc(notification)}
                            alt={getNotificationLabel(notification)}
                          />
                        </div>
                        <div
                          style={{
                            width: "calc(100% - 100px)",
                          }}
                        >
                          {getNotificationLabel(notification)}
                        </div>
                      </dt>
                    </div>
                  ))}
              </dl>
            </div>
            <AlertDialogFooter className="flex sm:justify-between items-center">
              <div
                className="flex items-center gap-2 cursor-pointer text-[14px] text-primary"
                onClick={() => {
                  document.location.href = "/dashboard/preference/notification";
                }}
              >
                <SettingsIcon /> {t("Mes préférences")}
              </div>
              <AlertDialogCancel>{t("Fermer")}</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Popover open={openProfilePopover} onOpenChange={setOpenProfilePopover}>
          <PopoverTrigger asChild>
            <div className="flex cursor-pointer items-center gap-1 text-muted hover:bg-foreground p-2 rounded-lg group transition-all">
              {user ? (
                <div className="flex gap-2 items-center">
                  {user.photo ? (
                    <div className="relative flex justify-center items-center    transition-all shrink-0 overflow-hidden size-10 rounded-lg">
                      <div className="w-full rounded-full aspect-square overflow-hidden">
                        <img
                          src={import.meta.env.VITE_MEDIA_BASE + photoUrl}
                          alt="photo de profil"
                          className="size-full object-cover"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="relative flex justify-center items-center bg-gray-100 group-hover:bg-background transition-all shrink-0 overflow-hidden size-10 rounded-lg">
                      <User2 className="group-hover:text-foreground" />
                    </div>
                  )}
                  {!isMobile && (
                    <div className="flex flex-col justify-center">
                      <p className="text-foreground m-0 group-hover:text-background">
                        {userDisplayName}
                      </p>
                      <p className="text-[10px] m-0 group-hover:text-background text-foreground opacity-45">
                        {user?.email}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <LoadingSpinner />
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-max z-[70]">
            <div className="mb-2 flex items-center gap-1">
              <User2 />
              <div>
                <p className="m-0 text-xs font-light">
                  {t("Connecté en tant que")}
                </p>
                <p className="m-0">
                  {user?.firstname} {user?.lastname}
                </p>
              </div>
            </div>
            <hr />
            <button
              disabled={localStorage.getItem("isImpersonating") === "true"}
              className="flex cursor-pointer items-center gap-2 py-2 text-muted-foreground transition-all hover:text-destructive disabled:opacity-50"
              onClick={() => {
                setOpenProfilePopover(false);
                setOpenEditProfil(true);
              }}
            >
              <UserPen size={20} />
              {t("Profil")}
            </button>

            <hr />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  disabled={user?.giveAccess}
                  className="flex cursor-pointer items-center gap-2 py-2 text-muted-foreground transition-all hover:text-destructive disabled:opacity-50"
                >
                  <KeyRound size={20} />
                  Donner accès à l’admin
                </button>
              </AlertDialogTrigger>

              <AlertDialogContent className="sm:max-w-[600px]">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-2xl">
                    Confirmer l’accès
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Vous êtes sur le point de donner un accès temporaire à un
                    Responsable Verdeo. Il pourra accéder à votre tableau de
                    bord pendant la durée choisie.
                  </AlertDialogDescription>
                </AlertDialogHeader>

                {/* Inputs jours / heures / minutes */}
                <div className="space-y-3 py-4">
                  <label className="text-sm font-medium">
                    Durée de l’accès
                  </label>

                  <div className="flex gap-3">
                    <div className="flex flex-row gap-2 items-center">
                      <Input
                        type="number"
                        min={0}
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        className="w-20"
                      />
                      <p className="text-lg text-muted-foreground text-center m-0">
                        j
                      </p>
                    </div>

                    <div className="flex flex-row gap-2 items-center">
                      <Input
                        type="number"
                        min={0}
                        max={23}
                        value={hours}
                        onChange={(e) => setHours(Number(e.target.value))}
                        className="w-20"
                      />
                      <p className="text-lg text-muted-foreground text-center m-0">
                        h
                      </p>
                    </div>

                    <div className="flex flex-row gap-2 items-center">
                      <Input
                        type="number"
                        min={0}
                        max={59}
                        step={5}
                        value={minutes}
                        onChange={(e) => setMinutes(Number(e.target.value))}
                        className="w-20"
                      />
                      <p className="text-lg text-muted-foreground text-center m-0">
                        min
                      </p>
                    </div>
                  </div>

                  {durationInMinutes >= 240 && (
                    <p className="text-xs text-orange-500">
                      ⚠️ Accès prolongé : assurez-vous que cela est nécessaire.
                    </p>
                  )}

                  <p className="text-xs text-muted-foreground">
                    Durée totale :{" "}
                    <span className="font-medium">
                      {durationInMinutes} minutes
                    </span>
                  </p>
                </div>

                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    disabled={isInvalidDuration}
                    onClick={() => {
                      postData({
                        url:
                          import.meta.env.VITE_API_ENDPOINT +
                          "/user/access/" +
                          user?.id,
                        data: {
                          impersonationDuration: durationInMinutes,
                        },
                      }).then((res) => {
                        if (res) {
                          toast({
                            title: "Accès donné avec succès",
                            description: `Durée : ${durationInMinutes} minutes`,
                          });
                        }
                      });
                    }}
                  >
                    Confirmer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <hr />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  disabled={localStorage.getItem("isImpersonating") === "true"}
                  className="flex cursor-pointer items-center gap-2 py-2 text-muted-foreground transition-all hover:text-destructive disabled:opacity-50"
                >
                  <LogOut size={20} />
                  {t("Déconnexion")}
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t("Êtes-vous sûr de vouloir vous déconnecter ?")}
                  </AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("Annuler")}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      sessionManager.logout();
                      logout(window.location.href);
                    }}
                  >
                    {t("Se déconnecter")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </PopoverContent>
        </Popover>
        <AlertDialog open={openEditProfil} onOpenChange={setOpenEditProfil}>
          <AlertDialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <AlertDialogHeader className="flex justify-between items-right">
              <AlertDialogTitle></AlertDialogTitle>
            </AlertDialogHeader>

            <Suspense fallback={<LoadingSpinner />}>
              <EditProfileForm closeModal={() => setOpenEditProfil(false)} />
            </Suspense>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default HeaderTop;
